import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { notification } from 'antd';

import ShiftService from '@src/services/ShiftService';

import type {
  OrganizationsReduxState,
  ShiftListItem,
  ShiftProviderProps,
  WorkJourneySummary,
} from '../../../types';
import ShiftContext from './ShiftContext';

const PAGE_SIZE = 10;
const shiftService = new ShiftService();

const ShiftProvider = ({ children }: ShiftProviderProps) => {
  const orgId = useSelector(
    ({ organizations }: OrganizationsReduxState) => organizations.organization.id,
  );
  const intl = useIntl();
  const [refresh, setRefresh] = useState(false);

  const getWorkJourneysByShiftId = useCallback(
    async (shiftId: string): Promise<WorkJourneySummary[] | null> => {
      try {
        return await shiftService.getWorkJourneysByShiftId(shiftId, orgId);
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
          placement: 'topRight',
        });
        return null;
      }
    },
    [intl, orgId],
  );

  const handleDelete = useCallback(
    async (shift: ShiftListItem) => {
      try {
        await shiftService.deleteShift(shift.id, orgId);
        setRefresh(prev => !prev);
        notification.success({
          message: intl.formatMessage({ id: 'shifts.delete.success' }),
        });
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
          placement: 'topRight',
        });
      }
    },
    [intl, orgId],
  );

  const value = {
    orgId,
    refresh,
    pageSize: PAGE_SIZE,
    PAGE_SIZE,
    handleDelete,
    getWorkJourneysByShiftId,
  };

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
};

export default ShiftProvider;
